// src/components/Navigation.tsx

import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useUserRole } from '../hooks/useUserRole';
import { logAuthenticationEvent } from '../lib/audit/auditLogger';

const Navigation: React.FC = () => {
  const { user, role } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      // Log logout before signing out (so we have user info)
      if (user && user.email && role) {
        logAuthenticationEvent(
          user.uid,
          user.email,
          role as 'admin' | 'staff' | 'client',
          'user_logout',
          true
        ).catch((err) => console.error('Failed to log logout event:', err));
      }
      
      await signOut(auth);
      navigate('/sign-in');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return null; // Don't show navigation if user is not authenticated
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav style={{
      backgroundColor: '#343a40',
      color: 'white',
      padding: '1rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
        <Link 
          to={role === 'admin' ? '/admin-dashboard' : role === 'staff' ? '/staff-dashboard' : '/client-dashboard'}
          style={{ 
            color: 'white', 
            textDecoration: 'none', 
            fontSize: '1.25rem', 
            fontWeight: 'bold' 
          }}
        >
          Help Keep My Money
        </Link>

        <div style={{ display: 'flex', gap: '1.5rem' }}>
          {/* Common routes for all authenticated users */}
          <Link 
            to="/profile"
            style={{ 
              color: isActive('/profile') || isActive('/profile-settings') ? '#ffc107' : 'white',
              textDecoration: 'none',
              fontWeight: isActive('/profile') || isActive('/profile-settings') ? 'bold' : 'normal'
            }}
          >
            Profile
          </Link>

          {/* Admin-specific routes */}
          {role === 'admin' && (
            <>
              <Link 
                to="/admin-dashboard"
                style={{ 
                  color: isActive('/admin-dashboard') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/admin-dashboard') ? 'bold' : 'normal'
                }}
              >
                Admin Dashboard
              </Link>
              <Link 
                to="/user-management"
                style={{ 
                  color: isActive('/user-management') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/user-management') ? 'bold' : 'normal'
                }}
              >
                Users
              </Link>
              <Link 
                to="/admin/documents"
                style={{ 
                  color: isActive('/admin/documents') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/admin/documents') ? 'bold' : 'normal'
                }}
              >
                Documents
              </Link>
              <Link 
                to="/admin/privacy-requests"
                style={{ 
                  color: isActive('/admin/privacy-requests') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/admin/privacy-requests') ? 'bold' : 'normal'
                }}
              >
                Privacy Requests
              </Link>
              <Link 
                to="/admin/glba"
                style={{ 
                  color: isActive('/admin/glba') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/admin/glba') ? 'bold' : 'normal'
                }}
              >
                GLBA Compliance
              </Link>
              <Link 
                to="/admin/audit-logs"
                style={{ 
                  color: isActive('/admin/audit-logs') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/admin/audit-logs') ? 'bold' : 'normal'
                }}
              >
                Audit Logs
              </Link>
            </>
          )}

          {/* Staff and Admin routes */}
          {(role === 'staff' || role === 'admin') && (
            <>
              {role === 'staff' && (
                <Link 
                  to="/staff-dashboard"
                  style={{ 
                    color: isActive('/staff-dashboard') ? '#ffc107' : 'white',
                    textDecoration: 'none',
                    fontWeight: isActive('/staff-dashboard') ? 'bold' : 'normal'
                  }}
                >
                  Staff Dashboard
                </Link>
              )}
              <Link 
                to="/clients"
                style={{ 
                  color: isActive('/clients') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/clients') ? 'bold' : 'normal'
                }}
              >
                Clients
              </Link>
              <Link 
                to="/tasks"
                style={{ 
                  color: isActive('/tasks') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/tasks') ? 'bold' : 'normal'
                }}
              >
                Tasks
              </Link>
            </>
          )}

          {/* Client-specific routes */}
          {role === 'client' && (
            <>
              <Link 
                to="/client-dashboard"
                style={{ 
                  color: isActive('/client-dashboard') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/client-dashboard') ? 'bold' : 'normal'
                }}
              >
                Dashboard
              </Link>
              <Link 
                to="/documents"
                style={{ 
                  color: isActive('/documents') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/documents') ? 'bold' : 'normal'
                }}
              >
                Documents
              </Link>
              <Link 
                to="/privacy-requests"
                style={{ 
                  color: isActive('/privacy-requests') ? '#ffc107' : 'white',
                  textDecoration: 'none',
                  fontWeight: isActive('/privacy-requests') ? 'bold' : 'normal'
                }}
              >
                Privacy Rights
              </Link>
            </>
          )}

          {/* Development/Testing - only show to admin */}
          {role === 'admin' && (
            <Link 
              to="/security-test"
              style={{ 
                color: isActive('/security-test') ? '#ffc107' : '#adb5bd',
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
            >
              Security Test
            </Link>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Link 
          to="/privacy-policy"
          style={{ 
            color: '#adb5bd',
            textDecoration: 'none',
            fontSize: '0.75rem'
          }}
        >
          Privacy Policy
        </Link>
        <Link 
          to="/glba-privacy-notice"
          style={{ 
            color: isActive('/glba-privacy-notice') || isActive('/glba-preferences') ? '#ffc107' : '#adb5bd',
            textDecoration: 'none',
            fontSize: '0.75rem',
            fontWeight: isActive('/glba-privacy-notice') || isActive('/glba-preferences') ? 'bold' : 'normal'
          }}
        >
          Financial Privacy
        </Link>
        <span style={{ fontSize: '0.875rem', color: '#adb5bd' }}>
          {user.email}
          {role && (
            <span style={{ 
              marginLeft: '0.5rem',
              padding: '0.25rem 0.5rem',
              borderRadius: '3px',
              backgroundColor: role === 'admin' ? '#dc3545' : role === 'staff' ? '#ffc107' : '#28a745',
              color: role === 'staff' ? 'black' : 'white',
              fontSize: '0.75rem'
            }}>
              {role.toUpperCase()}
            </span>
          )}
        </span>
        <button 
          onClick={handleLogout}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          Logout
        </button>
      </div>
    </nav>
  );
};

export default Navigation;



